/*
 Designed and developed by Richard Nesnass

 This file is part of SL+.

 SL+ is free software: you can redistribute it and/or modify
 it under the terms of the GNU Affero General Public License as published by
 the Free Software Foundation, either version 3 of the License, or
 (at your option) any later version.

 GPL-3.0-only or GPL-3.0-or-later

 SL+ is distributed in the hope that it will be useful,
 but WITHOUT ANY WARRANTY; without even the implied warranty of
 MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 GNU Affero General Public License for more details.

 You should have received a copy of the GNU Affero General Public License
 along with SL+.  If not, see <http://www.gnu.org/licenses/>.
 */
import { ref, computed, ComputedRef, Ref } from 'vue'
import router from '../router'
import { PersistedAppState, LocalUser, APIRequestPayload, DialogConfig, StoreResponse } from '@/models'
import { XHR_CONTENT_TYPE } from '../constants'
import { appVersion, LanguageCodes, ErrorCode, XHR_REQUEST_TYPE } from '../constants'
import useApiService from '@/api/api'
import { User } from '@models/index.ts'
import { createStore } from './dev-store'

const api = useApiService()

// ------------  Types --------------
interface UserPasswordLoginResponse {
  code: string
  user: User
}
interface AppStatus {
  validToken: boolean
  loading: boolean
  isMobileApp: boolean
}
interface AppState {
  validToken: boolean
  errorMessage: string
  loading: boolean
  fade: boolean
  lastLogin: Date
  currentLocalUser: LocalUser | undefined
  appIsOld: boolean
  languageCode: LanguageCodes
  disableDelays: boolean
  disableScenes: boolean
  dialogConfig: DialogConfig
}
// ------------  State (internal) --------------
const _appState: Ref<AppState> = ref({
  validToken: false,
  errorMessage: '',
  loading: false,
  fade: false,
  lastLogin: new Date(),
  currentLocalUser: undefined,
  appIsOld: false,
  languageCode: LanguageCodes.no,
  disableDelays: import.meta.env.VITE_DISABLE_DELAYS === 'true',
  disableScenes: false,
  dialogConfig: {
    title: '',
    text: '',
    visible: false,
    cancel: () => ({}),
    cancelText: 'Cancel',
    confirm: () => ({}),
    confirmText: 'Confirm',
  },
})

// This will be saved to device storage
const _persistedAppState: Ref<PersistedAppState> = ref({
  localUsers: {},
})

// ------------  Getters (Read only) --------------
interface Getters {
  status: ComputedRef<AppStatus>
  fade: ComputedRef<boolean>
  languageCode: ComputedRef<LanguageCodes>
  disableDelays: ComputedRef<boolean>
  disableScenes: ComputedRef<boolean>
  currentLocalUser: ComputedRef<LocalUser | undefined>
  persistedLocalUsers: ComputedRef<Record<string, LocalUser>>
  dialogConfig: ComputedRef<DialogConfig>
}
interface Actions {
  setDisableDelays: (s: boolean) => void
  setDisableScenes: (s: boolean) => void
  setLanguageCode: (lang: LanguageCodes) => void
  setError: (message: string) => void
  setLoading: (loading: boolean) => void
  setFade: (fade: boolean) => void
  setCurrentLocalUser: (user: LocalUser) => void
  logout: (rememberMe: boolean) => void
  tokenLogin: () => Promise<boolean>
  userPasswordLogin: (username: string, password: string) => Promise<boolean>
  smsLogin: (mobil: string) => Promise<void>
  smsCode: (query: Record<string, string>) => Promise<void>
  detectOldApp: () => Promise<void>
  setDialog: (visible: boolean, config?: DialogConfig) => void
  connectDeviceViaPIN: (pin: string) => Promise<StoreResponse>
}
interface ServiceInterface {
  actions: Actions
  getters: Getters
}
function useAppStore(): ServiceInterface {
  const getters: Getters = {
    status: computed(() => ({
      loading: _appState.value.loading,
      validToken: _appState.value.validToken,
      isMobileApp: navigator.userAgent.indexOf('Mobile') > -1,
    })),
    languageCode: computed(() => _appState.value.languageCode),
    // Current state of the display 'fade'
    fade: computed(() => _appState.value.fade),
    // TESTING ONLY. Remove app delays and show cheat functions
    disableDelays: computed(() => _appState.value.disableDelays),
    disableScenes: computed(() => _appState.value.disableScenes),
    currentLocalUser: computed(() => _appState.value.currentLocalUser),
    persistedLocalUsers: computed(() => _persistedAppState.value.localUsers),
    dialogConfig: computed(() => _appState.value.dialogConfig),
  }
  // ------------  Actions --------------

  const actions = {
    setDialog(visible: boolean, config?: DialogConfig): void {
      if (config) _appState.value.dialogConfig = config
      _appState.value.dialogConfig.visible = visible
    },

    // FOR TESTING ONLY. Remove app delays
    setDisableDelays(s: boolean) {
      _appState.value.disableDelays = s
    },
    setDisableScenes(s: boolean) {
      _appState.value.disableScenes = s
    },
    setLanguageCode: function (languageCode: LanguageCodes): void {
      _appState.value.languageCode = languageCode
    },
    setError: function (message: string): void {
      _appState.value.errorMessage = message
    },
    setLoading: function (loading: boolean): void {
      _appState.value.loading = loading
    },
    // Fade the display down or up
    setFade: function (fade: boolean): void {
      _appState.value.fade = fade
    },
    setCurrentLocalUser: function (user: LocalUser): void {
      _appState.value.currentLocalUser = user
      localStorage.setItem('jwt', user.jwt)
    },
    // The intention of logout is to enforce a new login with the server
    logout: function (rememberMe: boolean): void {
      if (_appState.value.currentLocalUser) {
        if (rememberMe) {
          _persistedAppState.value.localUsers[_appState.value.currentLocalUser.id] = _appState.value.currentLocalUser
        } else {
          delete _persistedAppState.value.localUsers[_appState.value.currentLocalUser.id]
        }
        _appState.value.currentLocalUser = undefined
      }
      localStorage.removeItem('jwt')
      window.location.assign('/auth/signout')
    },
    // Call server for the current version of the app
    detectOldApp: async function (): Promise<void> {
      const payload: APIRequestPayload<void> = {
        errorCode: ErrorCode.USER,
        method: XHR_REQUEST_TYPE.GET,
        credentials: false,
        route: '/api/appversion',
        contentType: XHR_CONTENT_TYPE.TEXT,
      }
      let version = ''
      try {
        const response = await api.base.apiRequest<void, string>(payload)
        if (!response.error && response.data) version = response.data
      } catch (error: unknown) {
        const e = (error as Error).message
        console.log(`Error getting server version: ${e}`)
      }
      if (appVersion !== version) {
        _appState.value.appIsOld = true
      }
    },
    // Try to exchange token for a session if the token already exists
    tokenLogin: function (): Promise<boolean> {
      return api.base
        .apiRequest({
          errorCode: ErrorCode.LOGIN,
          route: '/auth/token',
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
        })
        .then(async () => {
          // We now have an active session so proceed as normal..
          await router.push('/postlogin')
          return Promise.resolve(true)
        })
        .catch(() => {
          // Exchange was not accepted, clear the token and redirect to login page
          console.log('No valid token. Redirecting to login page..')
          _appState.value.currentLocalUser = undefined
          return Promise.resolve(false)
        })
    },
    // Request One Time Code by mobile number and password
    smsLogin: function (mobil: string): Promise<void> {
      return api.base
        .apiRequest({
          errorCode: ErrorCode.LOGIN,
          route: '/auth/sms/login',
          method: XHR_REQUEST_TYPE.POST,
          credentials: true,
          body: { mobil },
        })
        .then(() => {
          return
        })
    },
    // Log in with One Time Code
    smsCode: function (query: Record<string, string>): Promise<void> {
      return api.base
        .apiRequest<Record<string, string>, void>({
          errorCode: ErrorCode.LOGIN,
          route: '/auth/sms/code',
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          query,
        })
        .then(() => {
          return
        })
    },
    // Log in with username and password
    userPasswordLogin: function (username: string, password: string): Promise<boolean> {
      const client = getters.status.value.isMobileApp ? 'mobileApp' : 'webApp'
      return api.base
        .apiRequest<Record<string, string>, UserPasswordLoginResponse>({
          errorCode: ErrorCode.LOGIN,
          route: '/auth/password/login',
          method: XHR_REQUEST_TYPE.POST,
          credentials: true,
          body: { username, password, client },
        })
        .then((response) => {
          if (response.data && response.data.code) {
            localStorage.setItem('jwt', response.data.code)
            return actions.tokenLogin()
          } else {
            return Promise.resolve(true)
          }
        })
    },
    connectDeviceViaPIN: async function (pin: string): Promise<StoreResponse> {
      const res = await api.base.apiRequest({
        errorCode: ErrorCode.LOGIN,
        route: `/auth/tracking/signin`,
        method: XHR_REQUEST_TYPE.POST,
        credentials: true,
        contentType: XHR_CONTENT_TYPE.JSON,
        body: { pin: pin },
        requiresFeedback: false,
      })
      if (res.error) {
        const error = 'Error connecting device via PIN'
        console.error(res.error.cause ? res.error.cause : error)
      }
      // do not return success if PIN did not match
      else return { success: true }

      return { success: false, error: res.error }
    },
  }
  return {
    getters,
    actions,
  }
}

type AppStoreType = ReturnType<typeof useAppStore>
export type { AppStoreType, AppState }
export default createStore('app', useAppStore)
